import type { TFunction } from "i18next";

// Timestamps arrive as UTC ISO strings and are rendered in whichever zone the viewer picked.
// Nothing here ever constructs a Date from local parts: every conversion goes through
// Intl.DateTimeFormat with an explicit timeZone, so a viewer in Berlin and a viewer in
// Denver looking at the same block see the same instant labelled differently rather than two
// different instants.

export type ZoneMode = "local" | "station" | "utc";

export interface ZoneChoice {
  mode: ZoneMode;
  /** IANA name of the station's zone, from /api/settings/public. */
  station: string;
}

export function resolveZone(choice: ZoneChoice): string {
  switch (choice.mode) {
    case "utc":
      return "UTC";
    case "station":
      return choice.station;
    case "local":
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}

// Set from the i18n module on every language change. The formatter cache is keyed by
// locale already (see `key` below), so switching costs one extra Intl object, not a flush.
let displayLocale = "en-GB";

/** Called only from `web/src/i18n/index.ts`. */
export function setDisplayLocale(locale: string): void {
  displayLocale = locale;
}

// Formatters are expensive to construct and the canvas asks for several per frame, so they
// are memoised by the options that define them.
const cache = new Map<string, Intl.DateTimeFormat>();

function formatter(
  zone: string,
  options: Intl.DateTimeFormatOptions,
  locale = displayLocale,
): Intl.DateTimeFormat {
  const key = `${locale}|${zone}|${JSON.stringify(options)}`;
  let found = cache.get(key);
  if (!found) {
    found = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone });
    cache.set(key, found);
  }
  return found;
}

/** `27 May 2026` — the top line of a timeline label. */
export function formatDate(ms: number, zone: string): string {
  return formatter(zone, { day: "numeric", month: "short", year: "numeric" }).format(ms);
}

/** `10:13` — the bottom line, centred against the date above it. */
export function formatTime(ms: number, zone: string): string {
  return formatter(zone, { hour: "2-digit", minute: "2-digit", hour12: false }).format(ms);
}

/** `10:13:47` — used where the label has room and seconds matter, such as the readout. */
export function formatTimeSeconds(ms: number, zone: string): string {
  return formatter(zone, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(ms);
}

export function formatDateTime(ms: number, zone: string): string {
  return `${formatDate(ms, zone)} ${formatTime(ms, zone)}`;
}

/**
 * The zone's abbreviation at that instant — MDT in summer, MST in winter.
 *
 * Formatted as en-US rather than en-GB, which is what everything else here uses. The
 * abbreviation is locale data, not a property of the zone: en-GB renders America/Denver as
 * "GMT-6", which is both less informative and inconsistent with the station's own schedule
 * strings ("Tue 18:00-20:00 MDT").
 */
export function zoneLabel(ms: number, zone: string): string {
  const parts = formatter(zone, { timeZoneName: "short" }, "en-US").formatToParts(ms);
  return parts.find((part) => part.type === "timeZoneName")?.value ?? zone;
}

/** A zone-independent duration: clock notation for transport, labelled units for metadata. */
export function formatDuration(seconds: number, style: "clock" | "units" = "clock"): string {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (style === "units") {
    if (hours > 0) return `${hours}h ${minutes}m ${pad(secs)}s`;
    if (minutes > 0) return `${minutes}m ${pad(secs)}s`;
    return `${secs}s`;
  }
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}

/**
 * How far behind the live edge still counts as being at it.
 *
 * One constant for the whole app on purpose. The chat panel decides between tailing and
 * replaying on this, and the timeline's pill decides between saying LIVE and offering to go
 * there; two thresholds would let the page tail the stream while simultaneously offering to
 * take you to it, which is the same contradiction as having two live buttons.
 */
export const LIVE_DRIFT_MS = 2 * 60_000;

/** `2h 14m ago` — for the stream list, where exact times are a column of their own. */
export function formatRelative(ms: number, now: number, t: TFunction): string {
  const delta = Math.max(0, now - ms) / 1000;
  if (delta < 90) return t("time.justNow");
  if (delta < 3600) return t("time.minutesAgo", { count: Math.round(delta / 60) });
  if (delta < 86400) {
    const hours = Math.floor(delta / 3600);
    const minutes = Math.round((delta % 3600) / 60);
    return minutes
      ? t("time.hoursMinutesAgo", { hours, minutes })
      : t("time.hoursAgo", { count: hours });
  }
  const days = Math.round(delta / 86400);
  return days === 1 ? t("time.yesterday") : t("time.daysAgo", { count: days });
}
